import { useEffect } from 'react';
import { motion, useAnimate } from 'motion/react';
import { createPortal } from 'react-dom';

export interface LongPressIndicatorProps {
  x: number;
  y: number;
  active: boolean;
  fired: boolean;
  /** Duration of the growing animation in ms — should match totalDuration - startDelay. Default 500. */
  duration?: number;
  onBurstComplete: () => void;
}

// Growth accent — ties the "plant an event" gesture to --color-forest's documented meaning
const BURST_COLOR = 'var(--color-forest-bright)';
const BURST_RING = 'var(--color-forest-bright-ghost)';
const MAX_R = 185;

export function LongPressIndicator({
  x,
  y,
  active,
  fired,
  duration = 500,
  onBurstComplete,
}: LongPressIndicatorProps) {
  const [scope, animate] = useAnimate();

  // Fade in + start dot-field circle growing on mount
  useEffect(() => {
    animate(scope.current, { opacity: 1 }, { duration: 0.12 });
    // Grow the dot-field circle by animating its r attribute
    animate('#lp-main-circle', { r: MAX_R }, { duration: duration / 1000, ease: 'linear' });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Respond to cancel or fire
  useEffect(() => {
    if (fired) {
      // Slight scale-up then fade out — carries the "release" energy before modal appears
      animate(scope.current, { scale: 1.08, opacity: 0 }, { duration: 0.3, ease: 'easeOut' }).then(
        onBurstComplete
      );
    } else if (!active) {
      animate(scope.current, { opacity: 0 }, { duration: 0.22 }).then(onBurstComplete);
    }
  }, [active, fired]); // eslint-disable-line react-hooks/exhaustive-deps

  return createPortal(
    <div
      ref={scope}
      style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 9,
        opacity: 0,
      }}
    >
      <svg
        style={{
          position: 'fixed',
          inset: 0,
          width: '100dvw',
          height: '100dvh',
          overflow: 'visible',
        }}
      >
        <defs>
          {/* Repeating dot pattern — forest-green beads on transparent background */}
          <pattern id="lp-dots" x={0} y={0} width={11} height={11} patternUnits="userSpaceOnUse">
            <circle cx={5.5} cy={5.5} r={2} fill={BURST_COLOR} />
          </pattern>

          {/*
           * Radial gradient used as a mask:
           * - Opaque at center → full dot density visible
           * - Transparent at edge → dots dissolve naturally, no hard ring
           */}
          <radialGradient id="lp-mask-grad" cx={x} cy={y} r={MAX_R} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="white" stopOpacity={1} />
            <stop offset="40%" stopColor="white" stopOpacity={0.65} />
            <stop offset="75%" stopColor="white" stopOpacity={0.22} />
            <stop offset="100%" stopColor="white" stopOpacity={0} />
          </radialGradient>
          <mask id="lp-fade-mask" maskUnits="userSpaceOnUse">
            {/* Rect sized generously around press point to cover the full dot field */}
            <rect
              x={x - MAX_R - 10}
              y={y - MAX_R - 10}
              width={(MAX_R + 10) * 2}
              height={(MAX_R + 10) * 2}
              fill="url(#lp-mask-grad)"
            />
          </mask>
        </defs>

        {/*
         * Main dot field: starts at r=0, grows to MAX_R over `duration` ms.
         * Filled with the repeating dot pattern, faded by the radial mask.
         * Result: dense forest-green beads at center → sparse/invisible at edge.
         */}
        <circle
          id="lp-main-circle"
          cx={x}
          cy={y}
          r={0}
          fill="url(#lp-dots)"
          mask="url(#lp-fade-mask)"
        />

        {/* 3 sonar pulse rings — staggered, loop continuously */}
        {[0, 1, 2].map(i => (
          <motion.circle
            key={i}
            cx={x}
            cy={y}
            r={22}
            fill="none"
            stroke={BURST_RING}
            strokeWidth={1.5}
            initial={{ scale: 0.3, opacity: 0.6 }}
            animate={{ scale: 4.5, opacity: 0 }}
            style={{ transformOrigin: `${x}px ${y}px` }}
            transition={{
              duration: 1.1,
              delay: i * 0.35,
              repeat: Infinity,
              ease: 'easeOut',
            }}
          />
        ))}

        {/* Center anchor dot */}
        <circle cx={x} cy={y} r={5} fill={BURST_COLOR} />
      </svg>
    </div>,
    document.body
  );
}
